import { useState } from "react";
import { Badge, Button, Card, Group, SimpleGrid, Skeleton, Stack, Text, TextInput, Title } from "@mantine/core";
import { IconSparkles } from "@tabler/icons-react";
import { useMutation } from "@tanstack/react-query";
import type { IngredientExtractOutput, PassioResult } from "@passio/api-client";
import { passio } from "../lib/clients";
import { PageHeader } from "../theme/components";
import { EmptyState, ErrorNotice, RequireFeature } from "../components/common";
import { FoodDetailDrawer, FoodRow } from "../components/food";
import { FeedbackButtons } from "../components/FeedbackButtons";
import { RouteModelPicker } from "../components/ModelPicker";

const TOOLS = [
  { key: "invisible", title: "Hidden ingredients", route: "invisibleIngredientSuggestions", blurb: "Things in the dish nobody mentions: oils, butter, sugar, sauces." },
  { key: "possible", title: "Possible ingredients", route: "possibleIngredientSuggestions", blurb: "Ingredients this dish often includes, to confirm with the user." },
  { key: "predict", title: "Predicted ingredients", route: "predictIngredients", blurb: "A likely full ingredient list for the dish." },
] as const;
type ToolKey = (typeof TOOLS)[number]["key"];

const call = (k: ToolKey, dish: string) =>
  k === "invisible" ? passio.tools.invisibleIngredientSuggestions(dish) : k === "possible" ? passio.tools.possibleIngredientSuggestions(dish) : passio.tools.predictIngredients(dish);

function ToolColumn({ tool, m, onSelect }: { tool: (typeof TOOLS)[number]; m: { data?: PassioResult<IngredientExtractOutput[]>; isPending: boolean; error: unknown }; onSelect: (i: IngredientExtractOutput) => void }) {
  return (
    <Card>
      <Stack gap="xs">
        <Group justify="space-between" align="flex-start">
          <div>
            <Title order={3} fz={18}>
              {tool.title}
            </Title>
            <Text fz="xs" c="dimmed" ff="monospace">
              {tool.route}
            </Text>
          </div>
          {m.data && <Badge color="ink.8">{m.data.data.length}</Badge>}
        </Group>
        <Text fz="sm" c="dimmed">
          {tool.blurb}
        </Text>
        {m.error ? <ErrorNotice error={m.error} /> : null}
        {m.isPending && <Skeleton h={160} radius="md" />}
        {m.data && !m.isPending && (
          <>
            <Stack gap={2}>
              {m.data.data.map((it, i) => (
                <FoodRow key={i} item={it} onClick={it.refCode ? () => onSelect(it) : undefined} />
              ))}
              {m.data.data.length === 0 && <Text c="dimmed" fz="sm">Nothing suggested.</Text>}
            </Stack>
            <FeedbackButtons requestId={m.data.meta.requestId} compact />
          </>
        )}
      </Stack>
    </Card>
  );
}

export function DishIngredientsPage() {
  const [dish, setDish] = useState("");
  const [selected, setSelected] = useState<IngredientExtractOutput | null>(null);
  const invisible = useMutation({ mutationFn: (d: string) => call("invisible", d) });
  const possible = useMutation({ mutationFn: (d: string) => call("possible", d) });
  const predict = useMutation({ mutationFn: (d: string) => call("predict", d) });
  const ms = { invisible, possible, predict };
  const run = (d = dish) => {
    if (!d.trim()) return;
    setDish(d);
    invisible.mutate(d);
    possible.mutate(d);
    predict.mutate(d);
  };
  const any = invisible.data || possible.data || predict.data || invisible.isPending;

  return (
    <RequireFeature feature="text_ai">
      <PageHeader
        eyebrow="POST /napi/tools/invisibleIngredientSuggestions · possible… · predict…"
        title="Dish ingredients"
        description="Three views of what's in a dish, run side by side. Useful for prompting users to confirm oils, dressings and sides that change the numbers."
        actions={<RouteModelPicker routes={["invisibleIngredientSuggestions", "possibleIngredientSuggestions", "predictIngredients"]} />}
      />
      <Card mb="lg">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            run();
          }}
        >
          <Group gap="sm" wrap="nowrap">
            <TextInput style={{ flex: 1 }} size="md" placeholder="e.g. chicken tikka masala, caesar salad, banana bread" value={dish} onChange={(e) => setDish(e.currentTarget.value)} />
            <Button type="submit" size="md" leftSection={<IconSparkles size={16} />} loading={invisible.isPending || possible.isPending || predict.isPending}>
              Run all three
            </Button>
          </Group>
          <Group gap={6} mt="sm">
            {["chicken tikka masala", "caesar salad", "pad thai", "banana bread"].map((ex) => (
              <Badge key={ex} variant="outline" color="gray" tt="none" style={{ cursor: "pointer" }} onClick={() => run(ex)}>
                {ex}
              </Badge>
            ))}
          </Group>
        </form>
      </Card>
      {any ? (
        <SimpleGrid cols={{ base: 1, md: 3 }} spacing="md">
          {TOOLS.map((t) => (
            <ToolColumn key={t.key} tool={t} m={ms[t.key]} onSelect={setSelected} />
          ))}
        </SimpleGrid>
      ) : (
        <EmptyState title="Name a dish">Each column is its own API call, so you can compare responses and token usage in the inspector history.</EmptyState>
      )}
      <FoodDetailDrawer refCode={selected?.refCode} name={selected?.displayName} amount={selected} onClose={() => setSelected(null)} />
    </RequireFeature>
  );
}
